import { Box, Container, Stack, Button, Drawer, IconButton, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useAuth } from '@/hooks/auth';
import { NavLogo } from './NavLogo';
import { NavLinks } from './NavLinks';

export function PublicNavbar() {
  const scrollY = useScrollPosition();
  const { isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isScrolled = scrollY > 50;

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.3s ease',
          backgroundColor: isScrolled ? 'rgba(5, 5, 16, 0.85)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        }}
      >
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <NavLogo height={32} />

          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <NavLinks />
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {isAuthenticated ? (
              <>
                <Button
                  component={Link}
                  to="/"
                  variant="text"
                  sx={{ color: '#a0a5b5', textTransform: 'none', fontSize: 14, fontWeight: 500, '&:hover': { color: '#f0f0f5', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                >
                  Dashboard
                </Button>
                <Button
                  variant="contained"
                  onClick={logout}
                  sx={{
                    background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: '9999px',
                    px: 3,
                    py: 1,
                    '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s',
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/sign-in"
                  variant="text"
                  sx={{ color: '#a0a5b5', textTransform: 'none', fontSize: 14, fontWeight: 500, '&:hover': { color: '#f0f0f5', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                >
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: '9999px',
                    px: 3,
                    py: 1,
                    '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s',
                  }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Stack>

          <IconButton
            sx={{ display: { xs: 'flex', md: 'none' }, color: '#f0f0f5' }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Container>
      </Box>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0a0a1a',
            width: 300,
            borderLeft: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <NavLogo height={24} />
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#a0a5b5' }} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ p: 3 }}>
          <NavLinks
            orientation="vertical"
            onLinkClick={() => setMobileOpen(false)}
            fontSize={16}
          />
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            {isAuthenticated ? (
              <>
                <Button
                  component={Link}
                  to="/"
                  fullWidth
                  variant="text"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    color: '#a0a5b5',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    justifyContent: 'flex-start',
                    '&:hover': { color: '#f0f0f5', backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => { logout(); setMobileOpen(false); }}
                  sx={{
                    background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    borderRadius: '9999px',
                    py: 1.25,
                    '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)' },
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/sign-in"
                  fullWidth
                  variant="text"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    color: '#a0a5b5',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    justifyContent: 'flex-start',
                    '&:hover': { color: '#f0f0f5', backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/signup"
                  fullWidth
                  variant="contained"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    borderRadius: '9999px',
                    py: 1.25,
                    '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)' },
                  }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
